import type { TFunction } from 'i18next'
import { isValidEmail } from '@/lib/validations/common'
import {
  WEB_ROLES,
  type PortalUser,
  type UserRole,
  type UserStatus,
  type WebRole,
} from '@/types'

export interface UserFormValues {
  firstName: string
  lastName: string
  email: string
  title: string
  role: WebRole | ''
  status: UserStatus
}

export type UserFormFieldErrors = Partial<
  Record<
    | keyof Pick<UserFormValues, 'firstName' | 'lastName' | 'email' | 'role'>
    | 'avatar',
    string
  >
>

// Split a stored full_name into first / last (best-effort) for edit prefill.
export function splitFullName(full: string | null): {
  first: string
  last: string
} {
  const parts = (full ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { first: '', last: '' }
  if (parts.length === 1) return { first: parts[0], last: '' }
  return { first: parts[0], last: parts.slice(1).join(' ') }
}

export function portalUserToFormValues(
  user: PortalUser | null
): UserFormValues {
  const name = splitFullName(user?.full_name ?? null)
  return {
    firstName: name.first,
    lastName: name.last,
    email: user?.email ?? '',
    title: user?.title ?? '',
    role: (user?.role ?? '') as WebRole | '',
    status: user?.status === 'inactive' ? 'inactive' : 'active',
  }
}

export function validateUserForm(
  values: Pick<UserFormValues, 'firstName' | 'lastName' | 'email' | 'role'>,
  t: TFunction
): UserFormFieldErrors {
  const next: UserFormFieldErrors = {}
  if (!values.firstName.trim()) next.firstName = t('users.validation.required')
  if (!values.lastName.trim()) next.lastName = t('users.validation.required')
  if (!values.email.trim()) next.email = t('users.validation.required')
  else if (!isValidEmail(values.email.trim()))
    next.email = t('users.validation.emailInvalid')
  if (!values.role) next.role = t('users.validation.required')
  return next
}

// Manager cannot manage Admin accounts.
export function assignableRolesFor(
  viewerRole: UserRole | undefined
): WebRole[] {
  return WEB_ROLES.filter(r => !(viewerRole === 'Manager' && r === 'Admin'))
}
